import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Box, Typography, IconButton, useMediaQuery } from '@material-ui/core'
import { ExpandMore, ExpandLess } from '@material-ui/icons'

const ExperienceTimeline = ({ experiences }) => {
  const [expandedIndex, setExpandedIndex] = useState('all')
  const isMobile = useMediaQuery('(max-width: 768px)')

  const toggleExpand = (index) => {
    if (expandedIndex === 'all') {
      setExpandedIndex(expandedIndex === index ? null : index)
    } else {
      setExpandedIndex(expandedIndex === index ? null : index)
    }
  }

  const getYearFromDuration = (duration) => {
    const match = duration.match(/(\d{4})/)
    return match ? match[1] : ''
  }

  const getTimelineColor = (index, total) => {
    const colors = [
      '#00ffff', // Cyan
      '#ff00ff', // Magenta  
      '#00ff88', // Green
      '#ff8800', // Orange
      '#8800ff', // Purple
      '#00aaff'  // Blue
    ]
    return colors[index % colors.length]
  }

  const getTimelineGradient = (experiences) => {
    const totalExperiences = experiences.length
    let gradientStops = []

    experiences.forEach((_, index) => {
      const position = (index / (totalExperiences - 1)) * 100
      const color = getTimelineColor(index, totalExperiences)
      gradientStops.push(`${color} ${position}%`)
    })

    return `linear-gradient(180deg, ${gradientStops.join(', ')})`
  }

  return (
    <Box style={{
      position: 'relative',
      padding: isMobile ? '2rem 1rem' : '4rem 2rem',
      maxWidth: '1200px',
      margin: '0 auto'
    }}>
      {!isMobile && (
        <Box style={{
          position: 'absolute',
          left: '50%',
          top: 0,
          bottom: 0,
          width: '3px',
          background: getTimelineGradient(experiences),
          boxShadow: '0 0 20px rgba(0, 255, 255, 0.3)',
          transform: 'translateX(-50%)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '-1px',
            right: '-1px',
            bottom: 0,
            background: getTimelineGradient(experiences),
            filter: 'blur(2px)',
            zIndex: -1
          }
        }}/>
      )}

      {experiences.map((exp, index) => {
        const isLeft = index % 2 === 0
        const year = getYearFromDuration(exp.duration)
        const isExpanded = expandedIndex === 'all' || expandedIndex === index

        return (
          <motion.div
            key={index}
            initial={{ opacity: 0, x: isMobile ? 0 : (isLeft ? -100 : 100), y: isMobile ? 20 : 0 }}
            whileInView={{ opacity: 1, x: 0, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.6, delay: 0.2 }}
            style={{
              position: 'relative',
              marginBottom: isMobile ? '2rem' : '6rem',
              display: 'flex',
              justifyContent: isMobile ? 'center' : (isLeft ? 'flex-start' : 'flex-end')
            }}
          >
            <Box style={{
              width: isMobile ? '100%' : '45%',
              position: 'relative'
            }}>
              <motion.div
                whileHover={{ scale: 1.02 }}
                onClick={() => toggleExpand(index)}
                style={{
                  background: isExpanded
                    ? 'linear-gradient(135deg, rgba(0, 255, 255, 0.15) 0%, rgba(255, 0, 255, 0.15) 100%)'
                    : 'linear-gradient(135deg, rgba(0, 20, 30, 0.95) 0%, rgba(20, 0, 30, 0.95) 100%)',
                  border: `2px solid ${isExpanded ? '#00ffff' : 'rgba(0, 255, 255, 0.3)'}`,
                  borderRadius: '12px',
                  padding: isMobile ? '1.5rem' : '2rem',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: isExpanded
                    ? '0 0 40px rgba(0, 255, 255, 0.4), inset 0 0 20px rgba(0, 255, 255, 0.1)'
                    : '0 8px 32px rgba(0, 0, 0, 0.3)',
                  backdropFilter: 'blur(10px)'
                }}
              >
                {exp.domain && (
                  <img
                    src={`https://logo.clearbit.com/${exp.domain}`}
                    alt={`${exp.company} logo`}
                    style={{
                      width: isMobile ? '48px' : '64px',
                      height: isMobile ? '48px' : '64px',
                      objectFit: 'contain',
                      marginBottom: '1rem',
                      filter: 'brightness(1.2)'
                    }}
                    onError={(e) => {
                      e.target.onerror = null
                      e.target.src = `https://www.google.com/s2/favicons?domain=${exp.domain}&sz=128`
                    }}
                  />
                )}

                <Typography style={{
                  fontFamily: '"Orbitron", monospace',
                  fontSize: isMobile ? '1.2rem' : '1.5rem',
                  color: '#00ffff',
                  fontWeight: 'bold',
                  marginBottom: '0.5rem',
                  textShadow: '0 0 10px rgba(0, 255, 255, 0.5)'
                }}>
                  {exp.company}
                </Typography>

                <Typography style={{
                  fontFamily: '"Share Tech Mono", monospace',
                  fontSize: isMobile ? '0.95rem' : '1.1rem',
                  color: '#ff00ff',
                  marginBottom: '0.5rem',
                  textShadow: '0 0 10px rgba(255, 0, 255, 0.3)'
                }}>
                  {exp.position}
                </Typography>

                <Typography style={{
                  fontFamily: '"Share Tech Mono", monospace',
                  fontSize: isMobile ? '0.8rem' : '0.9rem',
                  color: '#888',
                  marginBottom: '1rem'
                }}>
                  {exp.duration}
                </Typography>

                <IconButton
                  size="small"
                  style={{
                    color: '#00ffff',
                    position: 'absolute',
                    top: '1rem',
                    right: '1rem'
                  }}
                >
                  {isExpanded ? <ExpandLess/> : <ExpandMore/>}
                </IconButton>

                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Box style={{
                        borderTop: '1px solid rgba(0, 255, 255, 0.3)',
                        paddingTop: '1rem',
                        marginTop: '1rem'
                      }}>
                        <ul style={{
                          listStyle: 'none',
                          padding: 0,
                          margin: 0
                        }}>
                          {exp.achievements.map((achievement, achIndex) => {
                            const achievementText = typeof achievement === 'string' ? achievement : ''
                            const isTechStack = achievementText.includes('Tech Stack:')

                            const techIcons = {
                              'AWS': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/amazonwebservices/amazonwebservices-original-wordmark.svg',
                              'Python': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg',
                              'JavaScript': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg',
                              'React': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg',
                              'PostgreSQL': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/postgresql/postgresql-original.svg',
                              'Golang': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/go/go-original.svg',
                              'Kubernetes': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/kubernetes/kubernetes-plain.svg',
                              'Azure': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/azure/azure-original.svg',
                              'Jenkins': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/jenkins/jenkins-original.svg',
                              'Terraform': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/terraform/terraform-original.svg',
                              'Ruby on Rails': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/rails/rails-plain.svg',
                              'GCP': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/googlecloud/googlecloud-original.svg',
                              'Redis': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/redis/redis-original.svg',
                              'DynamoDB': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/dynamodb/dynamodb-original.svg',
                              'FastAPI': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/fastapi/fastapi-original.svg',
                              'Docker': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg',
                              'MySQL': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mysql/mysql-original.svg',
                              'MongoDB': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongodb/mongodb-original.svg',
                              'GraphQL': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/graphql/graphql-plain.svg',
                              'Node': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg',
                              'TypeScript': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/typescript/typescript-original.svg',
                              'Git': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/git/git-original.svg',
                              'Nginx': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nginx/nginx-original.svg',
                              'Hadoop': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/hadoop/hadoop-original.svg',
                              'Hive': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/apache/apache-original.svg',
                              'SQL Server': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/microsoftsqlserver/microsoftsqlserver-plain.svg',
                              'Kafka': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/apachekafka/apachekafka-original.svg',
                              'Elasticsearch': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/elasticsearch/elasticsearch-original.svg'
                            }

                            const foundTechs = isTechStack ? Object.keys(techIcons).filter(tech =>
                              achievementText.toLowerCase().includes(tech.toLowerCase())
                            ) : []

                            return (
                              <li key={achIndex} style={{
                                fontFamily: '"Inter", "Roboto", sans-serif',
                                fontSize: isMobile ? '0.85rem' : '0.95rem',
                                color: '#ccc',
                                marginBottom: '0.75rem',
                                paddingLeft: '1.5rem',
                                position: 'relative',
                                lineHeight: 1.6
                              }}>
                                <span style={{
                                  position: 'absolute',
                                  left: 0,
                                  color: '#00ffff',
                                  textShadow: '0 0 5px rgba(0, 255, 255, 0.5)'
                                }}>●</span>
                                {typeof achievement === 'string' ? achievement :
                                  achievement.type === 'sublist' ? (
                                    <ul style={{ marginTop: '0.5rem', paddingLeft: '1rem' }}>
                                      {achievement.items.map((item, itemIndex) => (
                                        <li key={itemIndex} style={{
                                          marginBottom: '0.5rem',
                                          color: '#aaa'
                                        }}>
                                          {item}
                                        </li>
                                      ))}
                                    </ul>
                                  ) : achievement
                                }
                                {isTechStack && foundTechs.length > 0 && !isMobile && (
                                  <Box style={{
                                    display: 'flex',
                                    gap: '0.75rem',
                                    marginTop: '0.75rem',
                                    flexWrap: 'wrap'
                                  }}>
                                    {foundTechs.map((tech, techIdx) => (
                                      <img
                                        key={techIdx}
                                        src={techIcons[tech]}
                                        alt={tech}
                                        title={tech}
                                        style={{
                                          width: '32px',
                                          height: '32px',
                                          objectFit: 'contain',
                                          filter: 'brightness(1.2)',
                                          transition: 'transform 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.2)'}
                                        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                                      />
                                    ))}
                                  </Box>
                                )}
                              </li>
                            )
                          })}
                        </ul>
                      </Box>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            </Box>

            {!isMobile && (
              <Box style={{
                position: 'absolute',
                left: '50%',
                top: '2rem',
                transform: 'translateX(-50%)',
                zIndex: 100 + index // Ensure proper stacking order
              }}>
                {/* Animated pulse effect only */}
                <motion.div
                  whileHover={{ scale: 1.2 }}
                  whileTap={{ scale: 0.9 }}
                  animate={{
                    scale: isExpanded ? [1, 1.3, 1] : [1, 1.1, 1],
                    opacity: [0.7, 1, 0.7]
                  }}
                  transition={{
                    duration: isExpanded ? 1.5 : 2.5,
                    repeat: Infinity,
                    ease: 'easeInOut'
                  }}
                  style={{
                    width: '18px',
                    height: '18px',
                    borderRadius: '50%',
                    background: getTimelineColor(index, experiences.length),
                    boxShadow: `0 0 20px ${getTimelineColor(index, experiences.length)}, 0 0 40px ${getTimelineColor(index, experiences.length)}60`,
                    cursor: 'pointer',
                    position: 'relative',
                    zIndex: 1
                  }}
                  onClick={() => toggleExpand(index)}
                />

                {year && (
                  <Typography style={{
                    position: 'absolute',
                    left: isLeft ? '2rem' : 'auto',
                    right: isLeft ? 'auto' : '2rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fontFamily: '"Orbitron", monospace',
                    fontSize: '1.6rem',
                    color: getTimelineColor(index, experiences.length),
                    fontWeight: 'bold',
                    textShadow: `0 0 10px ${getTimelineColor(index, experiences.length)}80`,
                    whiteSpace: 'nowrap',
                    zIndex: 1
                  }}>
                    {year}
                  </Typography>
                )}
              </Box>
            )}
          </motion.div>
        )
      })}
    </Box>
  )
}

export default ExperienceTimeline
